import React from 'react';
import type { StructuredPresentation, StructuredSection } from './types';
import { ClinicalMarkdownBody } from './clinicalMarkdownComponents';
import { DataTable } from './DataTable';
import { KeyValueGrid } from './KeyValueGrid';
import { BadgeList } from './BadgeList';
import { CarePlanCard } from './CarePlanCard';
import { ChartBriefingCard } from './ChartBriefingCard';
import { AlertBanner } from './AlertBanner';

function mergePresentation(
  responsePresentation?: StructuredPresentation,
  sectionPresentation?: StructuredPresentation
): StructuredPresentation {
  return {
    layout: sectionPresentation?.layout ?? responsePresentation?.layout ?? 'auto',
    density: sectionPresentation?.density ?? responsePresentation?.density ?? 'comfortable',
    emphasis: sectionPresentation?.emphasis ?? responsePresentation?.emphasis ?? 'default',
    font_scale: sectionPresentation?.font_scale ?? responsePresentation?.font_scale ?? 'md',
    separators: sectionPresentation?.separators ?? responsePresentation?.separators ?? false,
  };
}

function proseScale(fontScale: StructuredPresentation['font_scale']): string {
  switch (fontScale) {
    case 'sm':
      return 'prose-p:text-[12.5px] prose-li:text-[12.5px] prose-h2:text-[14px] prose-h3:text-[13px]';
    case 'lg':
      return 'prose-p:text-[14.5px] prose-li:text-[14.5px] prose-h2:text-[16px] prose-h3:text-[15px]';
    default:
      return 'prose-p:text-[13.5px] prose-li:text-[13.5px] prose-h2:text-[15px] prose-h3:text-[14px]';
  }
}

function listScale(fontScale: StructuredPresentation['font_scale']): string {
  switch (fontScale) {
    case 'sm':
      return 'text-[12.5px]';
    case 'lg':
      return 'text-[14.5px]';
    default:
      return 'text-sm';
  }
}

function MarkdownListItem({ content }: { content: string }) {
  return (
    <div className="min-w-0 flex-1 leading-[1.65]">
      <ClinicalMarkdownBody
        content={content}
        className="prose prose-sm dark:prose-invert max-w-none [&_p]:m-0 [&_p]:leading-[1.65] [&_ul]:my-2 [&_ol]:my-2"
      />
    </div>
  );
}

function FlexibleSectionRenderer({
  section,
  hideTitle,
  variant,
  animDelay,
  SectionTitle,
  presentation,
}: {
  section: StructuredSection;
  hideTitle?: boolean;
  variant?: 'default' | 'plain';
  animDelay: string;
  SectionTitle: React.FC<{ title: string }>;
  presentation: StructuredPresentation;
}) {
  const rawPayload = section.data ?? section.items ?? section.rows ?? (section.content ? [{ content: section.content }] : null);
  const payload = Array.isArray(rawPayload) ? rawPayload : rawPayload != null ? [rawPayload] : null;
  if (!payload || payload.length === 0) return null;

  // Single object -> key-value grid
  if (payload.length === 1 && typeof payload[0] === 'object' && payload[0] !== null && !Array.isArray(payload[0])) {
    const obj = payload[0] as Record<string, unknown>;
    if (obj.content && typeof obj.content === 'string') {
      return (
        <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: animDelay }}>
          {!hideTitle && section.title && <SectionTitle title={section.title} />}
          <div className={`prose prose-sm dark:prose-invert max-w-none leading-relaxed
            prose-p:my-2.5 prose-p:leading-[1.8]
            prose-strong:text-foreground prose-em:text-foreground/80
            prose-ul:my-2.5 prose-ol:my-2.5 prose-li:my-1 prose-li:leading-[1.75]
            prose-table:my-3 prose-th:text-[11px] prose-th:tracking-[0.08em]
            ${proseScale(presentation.font_scale)}
            ${variant === 'plain' ? 'prose-p:text-[13px] prose-p:leading-[1.75]' : ''}`}>
            <ClinicalMarkdownBody content={obj.content as string} className="" />
          </div>
        </div>
      );
    }
    return (
      <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: animDelay }}>
        {!hideTitle && section.title && <SectionTitle title={section.title} />}
        <KeyValueGrid rows={[obj]} columns={2} />
      </div>
    );
  }

  // Array of primitives -> list
  if (Array.isArray(payload) && payload.length > 0 && payload.every((x) => typeof x !== 'object' || x === null)) {
    return (
      <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: animDelay }}>
        {!hideTitle && section.title && <SectionTitle title={section.title} />}
        <ul className={`space-y-1.5 text-foreground ${listScale(presentation.font_scale)}`}>
          {payload.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/50 shrink-0" />
              <span>{cellValue(s)}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Array of objects -> table if uniform keys, else cards
  if (Array.isArray(payload) && payload.length > 0 && payload.every((x) => typeof x === 'object' && x !== null && !Array.isArray(x))) {
    const rows = payload as Record<string, unknown>[];
    const allKeys = [...new Set(rows.flatMap((r) => Object.keys(r)))] as string[];
    const maxCols = 6;
    if (allKeys.length <= maxCols && rows.length >= 2) {
      return (
        <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: animDelay }}>
          {!hideTitle && section.title && <SectionTitle title={section.title} />}
          <DataTable columns={allKeys} rows={rows} compact={presentation.density === 'compact' || rows.length >= 8} />
        </div>
      );
    }
    return (
      <div className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: animDelay }}>
        {!hideTitle && section.title && <SectionTitle title={section.title} />}
        <div className="space-y-2 divide-y divide-border/40">
          {rows.map((item, i) => (
            <div
              key={i}
              className="pt-2 first:pt-0 text-sm"
            >
              <KeyValueGrid rows={[item]} columns={1} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // section.content (string)
  if (section.content) {
    return (
      <div className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ animationDelay: animDelay }}>
        {!hideTitle && section.title && <SectionTitle title={section.title} />}
        <div className={`prose prose-sm dark:prose-invert max-w-none leading-relaxed
          prose-p:my-2.5 prose-p:leading-[1.8]
          prose-strong:text-foreground prose-em:text-foreground/80
          prose-ul:my-2.5 prose-ol:my-2.5 prose-li:my-1 prose-li:leading-[1.75]
          prose-table:my-3 prose-th:text-[11px] prose-th:tracking-[0.08em]
          ${proseScale(presentation.font_scale)}`}>
          <ClinicalMarkdownBody content={section.content} className="" />
        </div>
      </div>
    );
  }

  return null;
}

function cellValue(v: unknown): string {
  if (v == null) return '—';
  if (typeof v === 'string' || typeof v === 'number') return String(v);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return JSON.stringify(v);
}

function shouldCompactList(meta: Record<string, unknown>, itemCount: number): boolean {
  return Boolean(meta.compact || itemCount >= 8);
}

export function ResponseSection({
  section,
  hideTitle = false,
  index = 0,
  variant = 'default',
  presentation: responsePresentation,
}: {
  section: StructuredSection;
  hideTitle?: boolean;
  index?: number;
  variant?: 'default' | 'plain';
  presentation?: StructuredPresentation;
}) {
  const meta = section.meta ?? {};
  const severityBadges = Boolean(meta.severity_badges || meta.flag_badges);
  const animDelay = `${Math.min(index * 50, 300)}ms`;
  const isPlain = variant === 'plain';
  const presentation = mergePresentation(responsePresentation, section.presentation);
  const compactDensity = presentation.density === 'compact';
  const titleClass = isPlain
    ? `font-medium tracking-tight text-foreground ${presentation.font_scale === 'lg' ? 'text-[15px]' : presentation.font_scale === 'sm' ? 'text-[13px]' : 'text-[14px]'}`
    : `font-medium uppercase tracking-[0.08em] text-text-tertiary flex items-center gap-2 ${presentation.font_scale === 'lg' ? 'text-[13px]' : 'text-[12px]'}`;

  function SectionTitle({ title }: { title: string }) {
    return (
      <h4 className={titleClass}>
        {title}
      </h4>
    );
  }

  switch (section.type) {
    case 'table':
      if (section.columns && section.rows?.length) {
        const compact = Boolean(meta.compact || compactDensity || section.rows.length >= 8);
        return (
          <div
            className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {!hideTitle && section.title && <SectionTitle title={section.title} />}
            <DataTable
              columns={section.columns}
              rows={section.rows as Record<string, unknown>[]}
              severityBadges={severityBadges}
              compact={compact}
            />
          </div>
        );
      }
      return null;

    case 'kv':
      if (section.rows?.length) {
        return (
          <div
            className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {!hideTitle && section.title && <SectionTitle title={section.title} />}
            <KeyValueGrid rows={section.rows as Record<string, unknown>[]} />
          </div>
        );
      }
      return null;

    case 'list':
      // #60: `items?.length` passes for a STRING (truthy .length, no .map) —
      // the exact "items.map is not a function" crash class. Array.isArray
      // everywhere items is mapped.
      if (Array.isArray(section.items) && section.items.length) {
        const strs = section.items.map((i) =>
          typeof i === 'string' ? i : cellValue(i)
        );
        const ordered = Boolean(meta.ordered);
        const ListTag = ordered ? 'ol' : 'ul';
        const compactList = compactDensity || shouldCompactList(meta as Record<string, unknown>, strs.length);
        return (
          <div
            className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {!hideTitle && section.title && <SectionTitle title={section.title} />}
            <ListTag className={`${isPlain || compactList ? 'space-y-1.5' : 'space-y-2'} text-foreground ${listScale(presentation.font_scale)}`}>
              {strs.map((s, i) => (
                <li
                  key={i}
                  className={
                    'flex items-start gap-2.5 py-0.5'
                  }
                >
                  <span className={`mt-1.5 shrink-0 ${ordered ? 'w-4 text-[11px] font-medium text-text-tertiary' : `h-1.5 w-1.5 rounded-full ${isPlain ? 'bg-foreground/55' : 'bg-primary/60'}`}`}>
                    {ordered ? `${i + 1}.` : null}
                  </span>
                  <MarkdownListItem content={s} />
                </li>
              ))}
            </ListTag>
          </div>
        );
      }
      return null;

    case 'badges':
      if (Array.isArray(section.items) && section.items.length) {
        const strs = section.items.map((i) =>
          typeof i === 'string' ? i : cellValue(i)
        );
        return (
          <div
            className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {!hideTitle && section.title && <SectionTitle title={section.title} />}
            <BadgeList items={strs} severityBadges={severityBadges} />
          </div>
        );
      }
      return null;

    case 'paragraph':
      if (section.content) {
        return (
          <div
            className="space-y-2 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {!hideTitle && section.title && <SectionTitle title={section.title} />}
            <div className={`prose prose-sm dark:prose-invert max-w-none leading-relaxed
              prose-headings:font-medium prose-headings:text-foreground prose-headings:tracking-tight
              prose-h2:mt-5 prose-h2:mb-2 ${isPlain ? '' : 'prose-h2:pb-1.5 prose-h2:border-b prose-h2:border-border/40'}
              prose-h3:mt-4 prose-h3:mb-1.5
              prose-p:text-foreground/85 prose-p:leading-[1.7]
              prose-strong:text-foreground prose-strong:font-medium
              prose-code:text-[color:var(--chart-categorical-2)] prose-code:bg-surface-sunken prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-xs
              prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-li:leading-[1.7]
              prose-table:text-sm prose-th:px-3 prose-th:py-2.5 prose-th:font-medium prose-th:text-xs prose-th:uppercase prose-th:tracking-wider prose-th:border-b prose-th:border-border/60
              prose-td:px-3 prose-td:py-2 prose-td:border-b prose-td:border-border/50
              prose-blockquote:border-l-brand prose-blockquote:bg-surface-sunken prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-blockquote:not-italic
              ${proseScale(presentation.font_scale)}
              ${isPlain || compactDensity ? 'prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5' : presentation.density === 'relaxed' ? 'prose-p:my-3 prose-ul:my-3 prose-ol:my-3' : ''}`}>
              <ClinicalMarkdownBody content={section.content} className="" />
            </div>
          </div>
        );
      }
      return null;

    case 'quote':
      if (section.content) {
        return (
          <blockquote
            className="border-l-4 border-sky-600 pl-4 py-2 my-2 text-sm text-text-tertiary italic
              bg-surface-sunken rounded-r-lg animate-in fade-in slide-in-from-left-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {section.content}
          </blockquote>
        );
      }
      return null;

    case 'alert':
      if (section.title) {
        return (
          <AlertBanner
            title={section.title}
            body={section.content ?? undefined}
          />
        );
      }
      return null;

    case 'cards':
      // #60 REOPEN (Eleanor round): `items?.length` passes for a STRING and
      // the accordion branch then maps it — the exact crash class the `list`
      // branch above was already guarded against. Array.isArray here too.
      if (Array.isArray(section.items) && section.items.length && meta.accordion) {
        const overviewAccordion = meta.chart_overview_accordion === true;
        const items = section.items as Array<{
          title?: string;
          status?: string;
          summary?: string;
          activities?: string[];
          target_date?: string;
          body_markdown?: string;
        }>;
        return (
          <div
            className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {!overviewAccordion && section.title && <SectionTitle title={section.title} />}
            {items.map((item, i) => (
              overviewAccordion ? (
                <ChartBriefingCard key={i} item={item} />
              ) : (
                <CarePlanCard key={i} item={item} />
              )
            ))}
          </div>
        );
      }
      if (Array.isArray(section.items) && section.items.length) {
        return (
          <div
            className="space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300"
            style={{ animationDelay: animDelay }}
          >
            {section.title && <SectionTitle title={section.title} />}
            <div className="space-y-2 divide-y divide-border/40">
              {section.items.map((item, i) => (
                <div
                  key={i}
                  className="pt-2 first:pt-0 text-sm"
                >
                  {typeof item === 'object' && item !== null && !Array.isArray(item)
                    ? (
                        <KeyValueGrid
                          rows={[item as Record<string, unknown>]}
                          columns={1}
                        />
                      )
                    : (
                        <span>{cellValue(item)}</span>
                      )}
                </div>
              ))}
            </div>
          </div>
        );
      }
      return null;

    case 'flexible':
      return (
        <FlexibleSectionRenderer
          section={section}
          hideTitle={hideTitle}
          variant={variant}
          animDelay={animDelay}
          SectionTitle={SectionTitle}
          presentation={presentation}
        />
      );

    default:
      // Unknown section type — try to render adaptively as flexible
      return (
        <FlexibleSectionRenderer
          section={{ ...section, type: 'flexible' }}
          hideTitle={hideTitle}
          variant={variant}
          animDelay={animDelay}
          SectionTitle={SectionTitle}
          presentation={presentation}
        />
      );
  }
}
